import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, Subscription } from 'rxjs';

import { System } from '../../../shared-components/System';
import { UnsavedChangesGuard } from '../../../shared-components/guards/unsaved-changes/unsaved-changes.guard';
import { CheckForErrorsGuard } from '../../../shared-components/guards/check-for-errors/check-for-errors.guard';

import { ScheduleFolderDTO, ScheduleFolderDetails } from './schedule-folder.model';
import { YearService } from '../../../shared-components/year/year.service';
import { map, take } from 'rxjs/operators';
import { LoginService } from '../../../shared-modules/login/login.service';
import { RolloverValidation } from './rollover-validation.model';

@Injectable()
export class ScheduleFolderService {

    // Subscriptions
    yearSubscription: Subscription;

    // School folders
    private _schoolSchedulingFolders: ScheduleFolderDTO[] = [];
    private observableSchoolSchedulingFolders = new ReplaySubject<ScheduleFolderDTO[]>(1);
    public observableSchoolSchedulingFoldersStream$ = this.observableSchoolSchedulingFolders.asObservable();

    get schoolSchedulingFolders(): ScheduleFolderDTO[] {
        return this._schoolSchedulingFolders;
    }

    set schoolSchedulingFolders(value: ScheduleFolderDTO[]) {
        this._schoolSchedulingFolders = value;
        if (this.selectedScheduleFolder) {
            const selectedCopy = this._schoolSchedulingFolders.find(folder =>
                folder.ID == this.selectedScheduleFolder.ID
            );
            if (selectedCopy) {
                this.selectedScheduleFolder = selectedCopy;
            }
        }
        this.observableSchoolSchedulingFolders.next(this._schoolSchedulingFolders);
    }

    // District Folder
    private _districtScheduleFolder: ScheduleFolderDTO;
    public observableDistrictScheduleFolder = new ReplaySubject<ScheduleFolderDTO>(1);
    observableDistrictScheduleFolderStream$ = this.observableDistrictScheduleFolder.asObservable();

    get districtScheduleFolder(): ScheduleFolderDTO {
        return this._districtScheduleFolder;
    }

    set districtScheduleFolder(value: ScheduleFolderDTO) {
        this._districtScheduleFolder = value;
        this.observableDistrictScheduleFolder.next(this._districtScheduleFolder);
    }

    // Selected Schedule Folder
    private _selectedScheduleFolder: ScheduleFolderDTO;
    public observableSelectedScheduleFolder = new ReplaySubject<ScheduleFolderDTO>(1);
    observableSelectedScheduleFolderStream$ = this.observableSelectedScheduleFolder.asObservable();

    get selectedScheduleFolder(): ScheduleFolderDTO {
        return this._selectedScheduleFolder;
    }

    set selectedScheduleFolder(value: ScheduleFolderDTO) {
        this._selectedScheduleFolder = value;
        this.observableSelectedScheduleFolder.next(this._selectedScheduleFolder);
    }

    constructor(
        private http: HttpClient,
        private yearService: YearService,
        private unsavedChangesGuard: UnsavedChangesGuard,
        private checkForErrorsGuard: CheckForErrorsGuard,
        private loginService: LoginService
    ) {
    }


    // Service Methods
    maintainSchoolFolders() {
        // POST: updates the folders along with the year
        if (!this.yearSubscription) {
            this.yearSubscription = this.yearService.observableDefaultYearStream$.subscribe(() => {
                this.resetScheduleFolders();
            });
            this.resetScheduleFolders();
        }
    }

    resetScheduleFolders() {
        // POST: reobtains the folders and sets the associated properties

        // Obtaining the district folder
        this.getDistrictScheduleFolder().subscribe(districtFolder => {
            if (districtFolder) {
                this.districtScheduleFolder = districtFolder;
            } else {
                this.districtScheduleFolder = undefined;
            }
        });

        // Obtaining the school folders
        this.getScheduleFolders().subscribe(schoolFolders => {

            // Setting the school folders, while also sorting them
            if (schoolFolders && schoolFolders.length > 0) {
                this.schoolSchedulingFolders = schoolFolders.sort((a, b) => {
                    if (a.Type > b.Type) {
                        return -1;
                    } else if (b.Type > a.Type) {
                        return 1;
                    } else {
                        if (a.IsDefault && !b.IsDefault) {
                            return -1;
                        } else if (b.IsDefault && !a.IsDefault) {
                            return 1;
                        } else {
                            return a.Name.localeCompare(b.Name);
                        }
                    }
                });
            } else {
                this.schoolSchedulingFolders = [];
            }

            // Setting the selected folder
            let newSelectedFolder = this.selectedScheduleFolder;
            if (this.schoolSchedulingFolders.length > 0) {
                if (newSelectedFolder) {
                    newSelectedFolder = this.schoolSchedulingFolders.find(schoolFolder => schoolFolder.ID == this.selectedScheduleFolder.ID);
                }
                if (!newSelectedFolder) {
                    const defaultSF = this.schoolSchedulingFolders.find(x => x.IsDefault == true);
                    if (defaultSF) {
                        newSelectedFolder = defaultSF;
                    } else {
                        newSelectedFolder = this.schoolSchedulingFolders[0];
                    }
                }
            } else {
                newSelectedFolder = undefined;
            }
            this.selectedScheduleFolder = newSelectedFolder;
        });
    }

    // Http Methods
    getDistrictScheduleFolder(): Observable<ScheduleFolderDTO> {
        const queryString = '?yearId=' + this.yearService.defaultYear.Id;
        return this.http.get<ScheduleFolderDTO>(System.apiURL.Counselor + 'GetDistrictScheduleFolder' + queryString)
            .pipe(
                map(folder => {
                    folder = System.convert(folder, new ScheduleFolderDTO());
                    return folder;
                })
            );
    }

    getScheduleFolders(
        siteId = this.yearService.defaultYear.SchoolID,
        yearId = this.yearService.defaultYear.Id,
        allScheduleFolders = false
    ): Observable<ScheduleFolderDTO[]> {
        const queryString = '?siteId=' + siteId + '&yearId=' + yearId + '&allScheduleFolders=' + allScheduleFolders;
        return this.http.get<ScheduleFolderDTO[]>(System.apiURL.Counselor + 'GetScheduleFolders' + queryString)
            .pipe(
                map(folders => {
                    for (let i = 0; i < folders.length; i++) {
                        folders[i] = System.convert(folders[i], new ScheduleFolderDTO());
                    }
                    return folders;
                })
            );
    }

    getScheduleFolderDetails(siteId: number, yearId: number): Observable<ScheduleFolderDetails[]> {
        const queryString = '?siteId=' + siteId + '&yearId=' + yearId;
        return this.http.get<ScheduleFolderDetails[]>(System.apiURL.Counselor + 'GetScheduleFolderDetails' + queryString).pipe(
            map(results => {
                for (let i = 0; i < results.length; i++) {
                    results[i] = System.convert(results[i], new ScheduleFolderDetails());
                }
                return results;
            })
        );
    }

    addEditScheduleFolder(siteId: number, yearId: number, scheduleFolder: ScheduleFolderDTO): Observable<number> {
        const queryString = '?siteId=' + siteId + '&yearId=' + yearId;
        return this.http.post<number>(System.apiURL.Counselor + 'AddEditScheduleFolder' + queryString, scheduleFolder);
    }

    copyScheduleFolder(siteId: number, yearId: number, sourceScheduleFolderId: number, newFolderName: string): Observable<ScheduleFolderDTO> {
        const queryString = '?siteId=' + siteId + '&yearId=' + yearId + '&sourceScheduleFolderId=' + sourceScheduleFolderId + '&newFolderName=' + newFolderName;
        return this.http.get<ScheduleFolderDTO>(System.apiURL.Counselor + 'CopyScheduleFolder' + queryString);
    }

    activateScheduleFolder(siteId: number, yearId: number, scheduleFolderId: number): Observable<boolean> {
        const queryString = '?siteId=' + siteId + '&yearId=' + yearId + '&scheduleFolderId=' + scheduleFolderId;
        return this.http.get<boolean>(System.apiURL.Counselor + 'ActivateScheduleFolder' + queryString);
    }

    deactivateScheduleFolder(siteId: number, yearId: number, scheduleFolderId: number): Observable<boolean> {
        const queryString = '?siteId=' + siteId + '&yearId=' + yearId + '&scheduleFolderId=' + scheduleFolderId;
        return this.http.get<boolean>(System.apiURL.Counselor + 'DeactivateScheduleFolder' + queryString);
    }

    deleteScheduleFolder(scheduleFolderId: number): Observable<string> {
        const queryString = '?scheduleFolderId=' + scheduleFolderId;
        return this.http.get<string>(System.apiURL.Counselor + 'DeleteScheduleFolder' + queryString);
    }

    rolloverScheduleFolder(scheduleFolderName: string): Observable<RolloverValidation[]> {
        const queryString = '?siteYearId=' + (this.loginService.LoginSettings.V10 ? this.yearService.defaultYear.Id : this.yearService.defaultYear.SiteYearId)
            + '&folderName=' + scheduleFolderName;
        return this.http.get<RolloverValidation[]>(System.apiURL.Counselor + 'RolloverScheduleFolder' + queryString);
    }

    isClassicFolderActive(yearId, siteId): Observable<boolean> {
        const queryString = '?yearId=' + yearId + '&siteId=' + siteId;
        return this.http.get<boolean>(System.apiURL.Counselor + 'IsClassicFolderActive' + queryString);
    }
}
